import { HospitalizationFilterDTO } from '../../dto/hospitalizationSummary/HospitalizationSummaryDTO'
import { toISOString } from '../../utils/localeHelpers'

/**
 * Filtros de la pantalla «Indicadores de internación» que no son de columna: el período y los de «Más filtros». Viajan al
 * backend en `filters` (los mismos en el resumen, la lista, las evoluciones y el export); los de columna de la barra
 * «+ Filtro» viajan aparte, en el `loadConfigDTO` de la grilla.
 */

/** Los filtros de «Más filtros» (`null` es «Todos»; las cunas, apagadas, quedan fuera del indicador general). */
export interface HospitalizationSelectFilters {
  sectorId: number | null
  admissionTypeId: number | null
  financierId: number | null
  includeNurseryBeds: boolean
}

export type HospitalizationFilterField = keyof HospitalizationSelectFilters

/** Los filtros de «Más filtros», en el orden de la pantalla (y de los chips). */
export const HOSPITALIZATION_MORE_FILTERS: readonly HospitalizationFilterField[] = [
  'sectorId', 'admissionTypeId', 'financierId', 'includeNurseryBeds',
]

/** Todos los filtros de «Más filtros» sin elegir. */
export const NO_SELECT_FILTERS: HospitalizationSelectFilters = {
  sectorId: null,
  admissionTypeId: null,
  financierId: null,
  includeNurseryBeds: false,
}

/** Lo que hay en los filtros de la pantalla; las fechas pueden estar vacías mientras se escriben. */
export interface HospitalizationFiltersState extends HospitalizationSelectFilters {
  from: Date | null
  to: Date | null
}

const isoDay = (date: Date): string => toISOString(date)!.substring(0, 10)

/** Los filtros validados (con las dos fechas) listos para aplicar; `null` si falta alguna fecha. Lo que no se eligió no viaja. */
export function toAppliedFilters(filters: HospitalizationFiltersState): HospitalizationFilterDTO | null {
  if (!filters.from || !filters.to) return null
  const applied: HospitalizationFilterDTO = { from: isoDay(filters.from), to: isoDay(filters.to) }
  if (filters.sectorId !== null) applied.sectorId = filters.sectorId
  if (filters.admissionTypeId !== null) applied.admissionTypeId = filters.admissionTypeId
  if (filters.financierId !== null) applied.financierId = filters.financierId
  if (filters.includeNurseryBeds) applied.includeNurseryBeds = true
  return applied
}

export const sameApplied = (a: HospitalizationFilterDTO, b: HospitalizationFilterDTO): boolean =>
  a.from === b.from && a.to === b.to
  && a.sectorId === b.sectorId
  && a.admissionTypeId === b.admissionTypeId
  && a.financierId === b.financierId
  && (a.includeNurseryBeds ?? false) === (b.includeNurseryBeds ?? false)

/** Lo que identifica una consulta (período y filtros de la pantalla): clave del caché de las secciones que se piden aparte. */
export const filtersKey = (filters: HospitalizationFilterDTO): string => [
  filters.from, filters.to, filters.sectorId, filters.admissionTypeId, filters.financierId, filters.includeNurseryBeds ?? false,
].join('|')
